"use client";

import { cn } from "@/lib/utils";
import { Mic, MicOff } from "lucide-react";
import { type RefObject, useEffect, useMemo, useRef, useState } from "react";

type VoiceFieldMicVariant = "default" | "ecole" | "liceo";
type VoiceFieldMicTone = "default" | "recording";
type FieldState = "idle" | "connecting" | "listening" | "speaking";

interface VoiceFieldMicProps {
  active?: boolean;
  muted?: boolean;
  pending?: boolean;
  variant?: VoiceFieldMicVariant;
  hueSeed?: string | null;
  className?: string;
}

interface VoiceFieldBackdropProps {
  active?: boolean;
  pending?: boolean;
  audioLevel?: number;
  className?: string;
}

const VERTEX_SHADER = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = `
precision highp float;

uniform vec2 u_res;
uniform float u_time;
uniform float u_energy;
uniform float u_gather;
uniform float u_hue;
uniform vec3 u_warm;
uniform vec3 u_cool;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.02 + vec2(7.3, 3.9);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  float aspect = u_res.x / u_res.y;
  vec2 p = vec2(uv.x * aspect, uv.y);

  float breath = 0.5 + 0.5 * sin(u_time * 0.34);
  float drive = 0.18 + 0.82 * u_energy;
  float t = u_time * (0.055 + 0.12 * drive);
  float horizonY = mix(0.24, 0.42, u_gather);

  vec2 q = vec2(
    fbm(p * 1.55 + vec2(t * 0.7, -t * 0.35)),
    fbm(p * 1.55 + vec2(-t * 0.45, t * 0.62) + 4.6)
  );
  float flow = fbm(p * vec2(1.05, 2.25) + q * (0.85 + 1.2 * u_energy) + vec2(t, 0.0));
  float wave = (flow - 0.5) * (0.09 + 0.22 * u_energy);
  float d = abs(uv.y - horizonY - wave);

  float width = mix(0.028, 0.013, u_gather) + 0.018 * breath * (0.35 + 0.65 * u_energy);
  float core = smoothstep(width * 2.4, 0.0, d);
  float glow = exp(-d * mix(6.5, 12.5, u_gather)) * (0.34 + 0.64 * u_energy);

  float cx = abs(uv.x - 0.5);
  float contain = smoothstep(0.58, 0.10, cx);
  float lum = (core * (0.54 + 0.46 * breath) + glow) * contain;

  float coolMix = clamp(0.5 - 0.45 * u_hue + 0.22 * u_gather, 0.0, 1.0);
  vec3 tone = mix(u_warm, u_cool, coolMix);
  vec3 color = mix(tone * lum, vec3(1.0) * lum, core * 0.55);

  vec3 base = vec3(0.010, 0.010, 0.017) + vec3(0.010, 0.008, 0.014) * (1.0 - uv.y);
  color += base;
  color += (hash(gl_FragCoord.xy) - 0.5) * 0.005;

  gl_FragColor = vec4(color, 1.0);
}
`;

const STATE_TARGETS: Record<FieldState, { energy: number; gather: number }> = {
  idle: { energy: 0.18, gather: 0.0 },
  connecting: { energy: 0.38, gather: 0.75 },
  listening: { energy: 0.5, gather: 0.0 },
  speaking: { energy: 1.0, gather: 0.0 },
};

/** Procedural syllable-like pulses so the field feels alive without real audio input. */
function simulatedSpeechEnergy(elapsedSeconds: number, state: FieldState, hoverLevel = 0): number {
  if (state === "connecting") return hoverLevel * 0.22;

  const baseIntensity = state === "idle" ? 0.78 : state === "listening" ? 0.38 : 0.55;
  const intensity = baseIntensity * (1 + hoverLevel * 0.9);

  const tempo = 1 + hoverLevel * 0.35;
  const t = elapsedSeconds * tempo;

  const pulseA = Math.max(0, Math.sin(t * 8.4 + Math.sin(t * 0.7) * 2.1));
  const pulseB = Math.max(0, Math.sin(t * 11.2 + 1.7));
  const pulseC = Math.max(0, Math.sin(t * 5.6 + Math.sin(t * 1.3) * 3.0));

  const phrase = 0.55 + 0.45 * Math.sin(t * 0.85 + 0.4);
  const micro = 0.82 + 0.18 * Math.sin(t * 23.5);

  const raw = (pulseA * 0.55 + pulseB * 0.3 + pulseC * 0.28) * phrase * micro;
  return Math.min(0.95, raw * intensity + hoverLevel * 0.14);
}

function hueFromSeed(seed?: string | null): number {
  if (!seed) return 0;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return ((h % 1000) / 1000 - 0.5) * 1.1;
}

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

function MicFieldCanvas({
  state,
  variant,
  tone,
  audioLevel,
  hueSeed,
  intensity,
  hoveredRef,
}: {
  state: FieldState;
  variant: VoiceFieldMicVariant;
  tone: VoiceFieldMicTone;
  audioLevel?: number;
  hueSeed: string | null;
  intensity: number;
  hoveredRef: RefObject<boolean>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(state);
  const hueRef = useRef(hueFromSeed(hueSeed));
  const audioLevelRef = useRef<number | null>(null);

  stateRef.current = state;
  hueRef.current = hueFromSeed(hueSeed);
  audioLevelRef.current =
    typeof audioLevel === "number" ? Math.min(1, Math.max(0, audioLevel)) : null;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl =
      canvas.getContext("webgl", { antialias: false, alpha: false }) ||
      (canvas.getContext("experimental-webgl", {
        antialias: false,
        alpha: false,
      }) as WebGLRenderingContext | null);
    if (!gl) {
      canvas.style.display = "none";
      return;
    }

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    if (!vertex || !fragment) {
      canvas.style.display = "none";
      return;
    }

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      canvas.style.display = "none";
      return;
    }
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

    const aPos = gl.getAttribLocation(program, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uniforms = {
      res: gl.getUniformLocation(program, "u_res"),
      time: gl.getUniformLocation(program, "u_time"),
      energy: gl.getUniformLocation(program, "u_energy"),
      gather: gl.getUniformLocation(program, "u_gather"),
      hue: gl.getUniformLocation(program, "u_hue"),
      warm: gl.getUniformLocation(program, "u_warm"),
      cool: gl.getUniformLocation(program, "u_cool"),
    };

    const warm =
      tone === "recording"
        ? [1.0, 0.25, 0.3]
        : variant === "liceo"
          ? [0.882, 0.173, 0.149]
          : variant === "ecole"
            ? [1.0, 0.72, 0.18]
            : [1.0, 0.52, 0.22];
    const cool =
      tone === "recording"
        ? [1.0, 0.54, 0.38]
        : variant === "liceo"
          ? [0.0, 0.322, 0.608]
          : variant === "ecole"
            ? [0.24, 0.56, 0.8]
            : [0.3, 0.42, 0.72];
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
    const start = performance.now();
    let raf = 0;
    let running = true;
    let energy = STATE_TARGETS[stateRef.current].energy;
    let gather = STATE_TARGETS[stateRef.current].gather;
    let hue = hueRef.current;
    let hoverLevel = 0;

    const resize = () => {
      const width = Math.floor(canvas.clientWidth * dpr);
      const height = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
    };

    const draw = (now: number) => {
      const target = STATE_TARGETS[stateRef.current];
      const elapsedSeconds = (now - start) / 1000;
      const hoverTarget = hoveredRef.current ? 1 : 0;
      hoverLevel += (hoverTarget - hoverLevel) * (hoverTarget > hoverLevel ? 0.14 : 0.08);

      const liveAudioLevel = audioLevelRef.current;
      const speechBoost = reducedMotion
        ? hoverLevel * 0.18
        : liveAudioLevel === null
          ? simulatedSpeechEnergy(elapsedSeconds, stateRef.current, hoverLevel)
          : liveAudioLevel * 0.82 + hoverLevel * 0.12;
      const targetEnergy = Math.min(1, target.energy + speechBoost);

      energy += (targetEnergy - energy) * (targetEnergy > energy ? 0.085 : 0.035);
      gather += (target.gather - gather) * 0.045;
      hue += (hueRef.current - hue) * 0.03;

      gl.uniform2f(uniforms.res, canvas.width, canvas.height);
      gl.uniform1f(uniforms.time, (now - start) / 1000);
      gl.uniform1f(uniforms.energy, energy);
      gl.uniform1f(uniforms.gather, gather);
      gl.uniform1f(uniforms.hue, hue);
      gl.uniform3f(uniforms.warm, warm[0], warm[1], warm[2]);
      gl.uniform3f(uniforms.cool, cool[0], cool[1], cool[2]);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    if (reducedMotion) {
      draw(start + 4200);
    } else {
      const loop = (now: number) => {
        if (!running) return;
        draw(now);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      observer.disconnect();
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [tone, variant, hoveredRef]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 h-full w-full"
      style={{ opacity: intensity }}
    />
  );
}

export function VoiceFieldMic({
  active = false,
  muted = false,
  pending = false,
  variant = "default",
  hueSeed = null,
  className,
}: VoiceFieldMicProps) {
  const state: FieldState = muted ? "idle" : pending ? "connecting" : active ? "listening" : "idle";
  const hoveredRef = useRef(false);
  const [isHovered, setIsHovered] = useState(false);

  const setHovered = (next: boolean) => {
    hoveredRef.current = next;
    setIsHovered(next);
  };

  const baseGlow = useMemo(() => (active || pending ? 0.26 : 0.12), [active, pending]);
  const glow = baseGlow + (isHovered ? 0.16 : 0);

  const isEcole = variant === "ecole";

  return (
    <span
      className={cn(
        "relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border bg-[#07070a] shadow-2xl ring-1 transition-[box-shadow,border-color,ring-color] duration-200 ease-out",
        muted
          ? "border-white/15 ring-white/10"
          : "border-white/15 ring-white/10 hover:border-white/30 hover:ring-white/25",
        isHovered && !muted && "border-white/30 ring-white/25",
        className
      )}
      style={{
        transform: `scale(${1 + baseGlow * 0.055})`,
        boxShadow:
          variant === "liceo"
            ? `0 0 ${26 + glow * 58}px rgba(0,82,155,${0.22 + glow * 0.34})`
            : isEcole
              ? `0 0 ${26 + glow * 58}px rgba(34,133,192,${0.22 + glow * 0.34})`
              : `0 0 ${24 + glow * 54}px rgba(249,115,22,${0.2 + glow * 0.32})`,
      }}
      onMouseEnter={() => !muted && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-hidden="true"
    >
      <MicFieldCanvas
        state={state}
        variant={variant}
        tone="default"
        hueSeed={hueSeed}
        intensity={0.68 + glow * 0.28}
        hoveredRef={hoveredRef}
      />
      <span
        className={cn(
          "absolute inset-0 opacity-70",
          variant === "liceo"
            ? "bg-[radial-gradient(circle_at_50%_48%,rgba(225,44,38,0.24),rgba(0,82,155,0.08)_45%,transparent_76%)]"
            : isEcole
              ? "bg-[radial-gradient(circle_at_50%_48%,rgba(255,182,6,0.24),rgba(34,133,192,0.08)_45%,transparent_76%)]"
              : "bg-[radial-gradient(circle_at_50%_48%,rgba(255,255,255,0.24),rgba(251,146,60,0.08)_45%,transparent_76%)]"
        )}
      />
      <span
        className={cn(
          "absolute h-12 w-12 rounded-full border transition-all duration-150",
          muted ? "border-white/10 bg-white/5" : "border-white/25 bg-black/20"
        )}
        style={{
          transform: `scale(${1 + baseGlow * 0.18})`,
          opacity: 0.52 + glow * 0.32,
        }}
      />
      {muted ? (
        <MicOff className="relative z-10 h-8 w-8 text-white/80" strokeWidth={2} />
      ) : (
        <Mic
          className="relative z-10 h-8 w-8 text-white drop-shadow-[0_0_18px_rgba(255,255,255,0.55)]"
          strokeWidth={2}
        />
      )}
    </span>
  );
}

/** Area-wide recording field driven by an existing recorder stream's audio level. */
export function VoiceFieldBackdrop({
  active = false,
  pending = false,
  audioLevel = 0,
  className,
}: VoiceFieldBackdropProps) {
  const hoveredRef = useRef(false);
  const state: FieldState = pending ? "connecting" : active ? "listening" : "idle";

  return (
    <span
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
      aria-hidden="true"
    >
      <MicFieldCanvas
        state={state}
        variant="default"
        tone="recording"
        audioLevel={audioLevel}
        hueSeed={null}
        intensity={0.86}
        hoveredRef={hoveredRef}
      />
      <span className="absolute inset-0 bg-[linear-gradient(90deg,rgba(23,19,24,0.86)_0%,rgba(23,19,24,0.46)_48%,rgba(23,19,24,0.76)_100%)]" />
    </span>
  );
}
